import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import api, { API_URL } from "../services/api";
import SEO from "../components/SEO";
import {
  UploadCloudIcon,
  AlertCircleIcon,
  MapPinIcon,
  RefreshCwIcon
} from "../components/Icons";

function Apply({ user, onApplySubmitted }) {
  const [currentStep, setCurrentStep] = useState(1); // 1 to 6
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [docUploadState, setDocUploadState] = useState({
    aadhaar_front: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
    aadhaar_back: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
    pan: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
    gst: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
  });
  const [docSuccessNotice, setDocSuccessNotice] = useState("");

  // 1. Business Info
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("individual");
  const [phone, setPhone] = useState(user?.number || "");
  const [email, setEmail] = useState(user?.email || "");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  // Shop GPS Location Auto-Detection
  const [coords, setCoords] = useState(null); // { lat, lng }
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | detecting | detected | error
  const [gpsMessage, setGpsMessage] = useState("");

  // Godown / Warehouse Address
  const [hasGodown, setHasGodown] = useState(false);
  const [godownContactName, setGodownContactName] = useState("");
  const [godownPhone, setGodownPhone] = useState("");
  const [godownStreet, setGodownStreet] = useState("");
  const [godownCity, setGodownCity] = useState("");
  const [godownState, setGodownState] = useState("");
  const [godownPincode, setGodownPincode] = useState("");

  const detectShopLocation = async (isManual = false) => {
    if (!navigator.geolocation) {
      setGpsStatus("error");
      setGpsMessage("Geolocation not supported on this browser.");
      return;
    }

    setGpsStatus("detecting");
    setGpsMessage("Detecting exact shop GPS coordinates...");

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setGpsStatus("detected");

        // Reverse-geocode to prefill inputs
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
            headers: { 'Accept-Language': 'en' }
          });
          const data = await res.json();
          if (data && data.address) {
            const a = data.address;
            const detectedCity = a.city || a.town || a.village || a.county || a.state_district || "";
            const detectedState = a.state || "";
            const detectedPincode = a.postcode || "";
            const detectedStreet = [a.road, a.suburb, a.neighbourhood].filter(Boolean).join(", ");

            if (detectedCity) setCity(prev => prev && !isManual ? prev : detectedCity);
            if (detectedState) setState(prev => prev && !isManual ? prev : detectedState);
            if (detectedPincode) setPincode(prev => prev && !isManual ? prev : detectedPincode);
            if (detectedStreet) setStreet(prev => prev && !isManual ? prev : detectedStreet);
            setGpsMessage(`Location Pinned: ${detectedCity}${detectedState ? ', ' + detectedState : ''} (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
          } else {
            setGpsMessage(`GPS coordinates pinned (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
          }
        } catch {
          setGpsMessage(`GPS coordinates pinned (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
        }
      },
      (err) => {
        setGpsStatus("error");
        setGpsMessage("GPS permission not granted or timeout. You can enter address manually or click Retry.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    // Automatically fetch shop location on start
    detectShopLocation(false);
  }, []);

  // 2. Identity Info
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");

  // 3. Document Files (Uploads)
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState("");
  const [aadhaarBackFile, setAadhaarBackFile] = useState("");
  const [panFile, setPanFile] = useState("");
  const [gstFile, setGstFile] = useState("");

  // 4. Bank & Payout
  const [accountHolderName, setAccountHolderName] = useState(`${user?.firstName || ""} ${user?.lastName || ""}`.trim());
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [showAcc, setShowAcc] = useState(false);
  const [showConfirmAcc, setShowConfirmAcc] = useState(false);
  const [ifsc, setIfsc] = useState("");
  const [accountType, setAccountType] = useState("current");
  const [upiId, setUpiId] = useState("");
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [ifscSuccess, setIfscSuccess] = useState(null);

  // 5. Consents
  const [consentAccuracy, setConsentAccuracy] = useState(false);
  const [consentVerification, setConsentVerification] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);

  const getFullFileUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("public")) return `${API_URL}/${path}`;
    return `${API_URL}/${path.replace(/^\//, "")}`;
  };

  // Live IFSC lookup in application flow
  const handleIfscLookup = async (val) => {
    const clean = val.toUpperCase().trim();
    setIfsc(clean);
    setIfscError("");
    setIfscSuccess(null);

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (clean.length === 11) {
      if (!ifscRegex.test(clean)) {
        setIfscError("Invalid IFSC format. Must be 4 letters, 0, and 6 alphanumeric characters.");
        return;
      }

      try {
        setIfscLoading(true);
        const res = await api.get(`/vendor/bank/ifsc/${clean}`);
        if (res.data && res.data.success) {
          setBankName(res.data.bankName || "");
          setIfscSuccess({
            bankName: res.data.bankName,
            branch: res.data.branch,
            city: res.data.city,
            state: res.data.state,
          });
        }
      } catch (err) {
        setBankName("");
        setIfscError(
          err.response?.data?.msg || "Invalid IFSC code. Bank branch not found."
        );
      } finally {
        setIfscLoading(false);
      }
    } else {
      setBankName("");
    }
  };

  // Format Aadhaar with spaces: XXXX XXXX XXXX
  const handleAadhaarChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
    const parts = raw.match(/.{1,4}/g) || [];
    setAadhaarNumber(parts.join(" "));
  };

  // Handle single file upload with interactive processing animation and feedback
  const handleFileUpload = async (e, docKey) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (<= 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("File is too large. Maximum supported size is 5MB.");
      return;
    }

    // Validate format
    const validMimes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validMimes.includes(file.type)) {
      setError("Please upload a valid JPEG, PNG, WEBP image or PDF document.");
      return;
    }

    setError("");
    setDocSuccessNotice("");
    const formattedSize =
      file.size > 1024 * 1024
        ? (file.size / (1024 * 1024)).toFixed(2) + " MB"
        : (file.size / 1024).toFixed(1) + " KB";
    const localPreview = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";

    // Stage 1: Initiating upload with animated progress
    setDocUploadState((prev) => ({
      ...prev,
      [docKey]: {
        stage: "uploading",
        percent: 25,
        filename: file.name,
        size: formattedSize,
        previewUrl: localPreview,
      },
    }));

    const formData = new FormData();
    formData.append("document", file);

    const progressTimer = setInterval(() => {
      setDocUploadState((prev) => {
        const curr = prev[docKey]?.percent || 25;
        if (curr < 80) {
          return {
            ...prev,
            [docKey]: { ...prev[docKey], percent: curr + 15 },
          };
        }
        return prev;
      });
    }, 180);

    try {
      const res = await api.post("/vendor/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearInterval(progressTimer);

      if (res.data.success && res.data.filePath) {
        // Stage 2: Verification & scanning interactive animation
        setDocUploadState((prev) => ({
          ...prev,
          [docKey]: {
            ...prev[docKey],
            stage: "processing",
            percent: 90,
          },
        }));

        // Dynamic multi-stage laser scan feedback
        setTimeout(() => {
          setDocUploadState((prev) => ({
            ...prev,
            [docKey]: {
              ...prev[docKey],
              percent: 97,
            },
          }));
        }, 450);

        // Allow 950ms for the seller to visually see the interactive laser scanning & verification
        setTimeout(() => {
          if (docKey === "aadhaar_front") setAadhaarFrontFile(res.data.filePath);
          else if (docKey === "aadhaar_back") setAadhaarBackFile(res.data.filePath);
          else if (docKey === "pan") setPanFile(res.data.filePath);
          else if (docKey === "gst") setGstFile(res.data.filePath);

          // Stage 3: Completed state ("Document Added ✓")
          setDocUploadState((prev) => ({
            ...prev,
            [docKey]: {
              ...prev[docKey],
              stage: "completed",
              percent: 100,
            },
          }));
          const label =
            docKey === "aadhaar_front"
              ? "Aadhaar Card (Front)"
              : docKey === "aadhaar_back"
              ? "Aadhaar Card (Back)"
              : docKey === "pan"
              ? "PAN Card"
              : "GST Certificate";
          setDocSuccessNotice(`${label} added successfully! ✓`);
          setTimeout(() => setDocSuccessNotice(""), 5000);
        }, 950);
      } else {
        throw new Error(res.data?.msg || "Failed to upload document file.");
      }
    } catch (err) {
      clearInterval(progressTimer);
      setError(err.response?.data?.msg || "Failed to upload document. Please try again.");
      setDocUploadState((prev) => ({
        ...prev,
        [docKey]: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
      }));
    }
  };

  const handleRemoveDoc = (docKey) => {
    if (docKey === "aadhaar_front") setAadhaarFrontFile("");
    else if (docKey === "aadhaar_back") setAadhaarBackFile("");
    else if (docKey === "pan") setPanFile("");
    else if (docKey === "gst") setGstFile("");

    setDocUploadState((prev) => ({
      ...prev,
      [docKey]: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
    }));
  };

  const renderDocumentUploadCard = (docKey, title, subtitle, required, fileValue) => {
    const uploadState = docUploadState[docKey] || { stage: "idle", percent: 0 };
    const isUploading = uploadState.stage === "uploading" || uploadState.stage === "processing";
    const isAdded = !isUploading && (!!fileValue || uploadState.stage === "completed");

    if (isUploading) {
      return (
        <div
          className="doc-processing-card"
          style={{
            border: "1.5px solid #3b82f6",
            borderRadius: "14px",
            padding: "20px 22px",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 6px 20px rgba(59, 130, 246, 0.16)",
          }}
        >
          {/* Animated radar scan laser sweeping across entire card */}
          <div className="doc-scan-laser" />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
            <div>
              <strong style={{ fontSize: "14.5px", color: "#0f172a", display: "block" }}>{title}</strong>
              <span style={{ fontSize: "12.5px", color: "#2563eb", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "7px" }}>
                <span className="spinner-border spinner-border-sm" role="status" style={{ width: "13px", height: "13px", borderWidth: "2px" }} />
                <span>
                  {uploadState.stage === "processing"
                    ? "Scanning Document Clarity, Edges & Authenticity..."
                    : "Securely Uploading to Encrypted Merchant Vault..."}
                </span>
              </span>
            </div>
            <span
              style={{
                background: "#dbeafe",
                color: "#1d4ed8",
                fontSize: "12.5px",
                fontWeight: "900",
                padding: "4px 12px",
                borderRadius: "10px",
                letterSpacing: "0.5px",
              }}
            >
              {uploadState.percent}%
            </span>
          </div>

          {/* Animated progress bar with gradient and glow */}
          <div style={{ width: "100%", height: "8px", background: "#e2e8f0", borderRadius: "8px", overflow: "hidden", marginBottom: "14px" }}>
            <div
              style={{
                width: `${uploadState.percent}%`,
                height: "100%",
                background: "linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)",
                borderRadius: "8px",
                transition: "width 0.25s ease-out",
                boxShadow: "0 0 10px rgba(59, 130, 246, 0.5)",
              }}
            />
          </div>

          {/* Interactive file status details with preview scanner */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              background: "#ffffff",
              padding: "12px 16px",
              borderRadius: "10px",
              border: "1px solid #cbd5e1",
            }}
          >
            {uploadState.previewUrl ? (
              <div className="doc-preview-wrapper" style={{ position: "relative", width: "56px", height: "42px", flexShrink: 0 }}>
                <img
                  src={uploadState.previewUrl}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px", border: "1px solid #94a3b8" }}
                />
                <div className="doc-scan-grid" />
                <div className="doc-scan-laser" />
              </div>
            ) : (
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "8px",
                  background: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  flexShrink: 0,
                }}
              >
                📄
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {uploadState.filename || "Processing selected document..."}
              </div>
              <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                {uploadState.size ? `${uploadState.size} • ` : ""}256-Bit Encrypted SSL Vault
              </div>
            </div>
            <span
              style={{
                fontSize: "11.5px",
                fontWeight: "800",
                color: "#2563eb",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                padding: "4px 10px",
                borderRadius: "8px",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span className="spinner-grow spinner-grow-sm" role="status" style={{ width: "8px", height: "8px" }} />
              Scanning...
            </span>
          </div>
        </div>
      );
    }

    if (isAdded) {
      return (
        <div
          className="doc-added-card"
          style={{
            border: "1.5px solid #86efac",
            borderRadius: "14px",
            padding: "20px 22px",
            background: "#f0fdf4",
            boxShadow: "0 4px 16px rgba(34, 197, 94, 0.1)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
            <div>
              <strong style={{ fontSize: "14.5px", color: "#0f172a", display: "block" }}>{title}</strong>
              <span style={{ fontSize: "12px", color: "#166534" }}>{subtitle}</span>
            </div>
            <span className="doc-badge-added">
              <span>✓</span>
              <span>Document Added</span>
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#ffffff",
              padding: "14px 16px",
              borderRadius: "10px",
              border: "1px solid #bbf7d0",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0, flex: 1 }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={uploadState.previewUrl || getFullFileUrl(fileValue)}
                  alt={title}
                  style={{ width: "64px", height: "46px", objectFit: "cover", borderRadius: "6px", border: "1.5px solid #86efac" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span
                  style={{
                    position: "absolute",
                    bottom: "-4px",
                    right: "-4px",
                    background: "#16a34a",
                    color: "#ffffff",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "900",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  ✓
                </span>
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "14px", color: "#15803d", fontWeight: "900" }}>
                    Document Added ✓
                  </span>
                  <span style={{ fontSize: "10.5px", background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: "12px", fontWeight: "800" }}>
                    Verified File
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "#334155", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: "2px" }}>
                  {uploadState.filename || (typeof fileValue === "string" ? fileValue.split("/").pop() : "document.png")}
                  {uploadState.size ? ` • ${uploadState.size}` : ""}
                </div>
                <div style={{ fontSize: "11.5px", color: "#16a34a", fontWeight: "700", marginTop: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span>●</span>
                  <span>Attached to Seller Application</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label
                style={{
                  margin: 0,
                  cursor: "pointer",
                  background: "#f8fafc",
                  color: "#0f172a",
                  border: "1px solid #cbd5e1",
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>🔄 Replace</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileUpload(e, docKey)}
                />
              </label>
              <button
                type="button"
                onClick={() => handleRemoveDoc(docKey)}
                style={{
                  background: "#fee2e2",
                  color: "#b91c1c",
                  border: "1px solid #fecaca",
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
                title="Remove document"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "18px 20px", background: "#f8fafc" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <strong style={{ fontSize: "14px", color: "#0f172a", display: "block" }}>{title}</strong>
            <span style={{ fontSize: "12px", color: "#64748b" }}>{subtitle}</span>
          </div>
          <span
            style={{
              background: required ? "#fee2e2" : "#f1f5f9",
              color: required ? "#b91c1c" : "#475569",
              fontSize: "11px",
              fontWeight: "800",
              padding: "3px 8px",
              borderRadius: "6px",
            }}
          >
            ● {required ? "Required" : "Optional"}
          </span>
        </div>

        <label
          className="btn btn-outline-secondary btn-sm"
          style={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "#ffffff",
            border: "1.5px dashed #cbd5e1",
            padding: "9px 18px",
            borderRadius: "8px",
            color: "#1e293b",
            fontWeight: "700",
            fontSize: "13px",
          }}
        >
          <UploadCloudIcon size={16} />
          <span>Upload {title}</span>
          <input
            type="file"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => handleFileUpload(e, docKey)}
          />
        </label>
        <div style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "6px" }}>
          Accepts JPG, PNG, WEBP or PDF (Max 5MB)
        </div>
      </div>
    );
  };

  // Step Validation logic
  const validateCurrentStep = () => {
    setError("");
    if (currentStep === 1) {
      // Account info - already logged in
      return true;
    }
    if (currentStep === 2) {
      // Business info
      if (!businessName.trim()) { setError("Please enter your Store / Business Name."); return false; }
      if (!phone.trim() || phone.trim().length !== 10) { setError("Please enter a valid 10-digit mobile number."); return false; }
      if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email address."); return false; }
      if (!street.trim() || !city.trim() || !state.trim() || !pincode.trim()) { setError("Please fill all fulfillment address fields."); return false; }
      return true;
    }
    if (currentStep === 3) {
      // Identity info
      const rawAadhaar = aadhaarNumber.replace(/\s+/g, "");
      if (rawAadhaar.length !== 12) { setError("Please enter a valid 12-digit Aadhaar Number."); return false; }
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(panNumber.trim().toUpperCase())) { setError("Please enter a valid 10-character PAN number (e.g. ABCDE1234F)."); return false; }
      return true;
    }
    if (currentStep === 4) {
      // Documents
      if (!aadhaarFrontFile) { setError("Please upload the Front Side of your Aadhaar Card."); return false; }
      if (!aadhaarBackFile) { setError("Please upload the Back Side of your Aadhaar Card."); return false; }
      if (!panFile) { setError("Please upload your PAN Card."); return false; }
      return true;
    }
    if (currentStep === 5) {
      // Bank details
      if (!accountHolderName.trim()) { setError("Please enter the Account Holder Name."); return false; }
      if (!bankName.trim()) { setError("Please enter your Bank Name."); return false; }
      if (!accountNumber.trim()) { setError("Please enter your Bank Account Number."); return false; }
      if (accountNumber.trim() !== confirmAccountNumber.trim()) { setError("Account numbers do not match."); return false; }
      if (!ifsc.trim() || ifsc.trim().length < 8) { setError("Please enter a valid IFSC Code."); return false; }
      return true;
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(6, prev + 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setError("");
    setCurrentStep((prev) => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentAccuracy || !consentVerification || !consentTerms) {
      setError("Please check all required consent boxes before submitting.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const documentsPayload = [
        {
          documentType: "aadhaar",
          documentName: "Aadhaar Card",
          documentNumber: aadhaarNumber.replace(/\s+/g, ""),
          documentFile: aadhaarFrontFile,
          frontImage: aadhaarFrontFile,
          backImage: aadhaarBackFile
        },
        {
          documentType: "pan",
          documentName: "PAN Card",
          documentNumber: panNumber.trim().toUpperCase(),
          documentFile: panFile,
          frontImage: panFile
        }
      ];

      if (gstFile) {
        documentsPayload.push({
          documentType: "gst",
          documentName: "GST Certificate",
          documentFile: gstFile
        });
      }

      await api.post("/vendor/apply", {
        businessName: businessName.trim(),
        businessType,
        phone: phone.trim(),
        email: email.trim().toLowerCase(),
        address: { street, city, state, pincode },
        location: coords ? { type: 'Point', coordinates: [coords.lng, coords.lat] } : null,
        latitude: coords?.lat,
        longitude: coords?.lng,
        godownAddress: {
          hasGodown,
          contactName: godownContactName.trim(),
          phone: godownPhone.trim(),
          street: godownStreet.trim(),
          city: godownCity.trim(),
          state: godownState.trim(),
          pincode: godownPincode.trim()
        },
        identity: {
          aadhaarNumber: aadhaarNumber.replace(/\s+/g, ""),
          panNumber: panNumber.trim().toUpperCase()
        },
        documents: documentsPayload,
        bankDetails: {
          accountHolderName: accountHolderName.trim(),
          bankName: bankName.trim(),
          accountNumber: accountNumber.trim(),
          ifsc: ifsc.trim().toUpperCase(),
          accountType,
          upiId: upiId.trim()
        },
        legalConsent: {
          confirmedAccuracy: consentAccuracy,
          authorizedVerification: consentVerification,
          agreedTerms: consentTerms
        }
      });

      if (onApplySubmitted) onApplySubmitted();
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to submit seller application. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendor_user");
    window.location.href = "/vendor/login";
  };

  const stepsList = [
    { num: 1, label: "Account" },
    { num: 2, label: "Business" },
    { num: 3, label: "Identity" },
    { num: 4, label: "Documents" },
    { num: 5, label: "Bank & Payout" },
    { num: 6, label: "Review & Consent" }
  ];

  return (
    <div className="onboarding-wrap" style={{ minHeight: "100vh", background: "#f8fafc", padding: "20px 16px 40px" }}>
      <SEO
        title="Seller Application & Store Onboarding | IM Online Seller Center"
        description="Complete your digital merchant verification to launch your online footwear store on IM Online. Simple KYC verification for merchants and brands."
        canonical="https://vendor.imonline.shop/vendor/apply"
        ogTitle="IM Online Seller Application & Onboarding"
      />

      {/* ═══════════ TOP UTILITY BAR (MOBILE & DESKTOP) ═══════════ */}
      <div className="onboarding-top-bar" style={{ maxWidth: "840px", margin: "0 auto 20px", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
        <a href="/vendor" style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
          <img src={logo} alt="IM Online" style={{ height: "30px", objectFit: "contain" }} />
          <span style={{ fontSize: "11px", fontWeight: "900", color: "#0f172a", letterSpacing: "1px" }}>SELLER CENTER</span>
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <a
            href="mailto:support@imonline.shop"
            style={{ fontSize: "12px", color: "#64748b", textDecoration: "none", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "4px" }}
          >
            <span>💬</span> <span className="d-none d-sm-inline">Seller Help</span>
          </a>
          <button
            type="button"
            onClick={handleLogout}
            style={{ fontSize: "12px", color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", padding: "4px 10px", borderRadius: "6px", fontWeight: "700", cursor: "pointer" }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* ═══════════ HEADER ═══════════ */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <div style={{ fontSize: "11px", fontWeight: "800", letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--primary)" }}>
          IM ONLINE SELLER ONBOARDING
        </div>
        <h2 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
          Launch Your Footwear Store
        </h2>
        <p style={{ fontSize: "13.5px", color: "#64748b", maxWidth: "520px", margin: "4px auto 0" }}>
          Complete 6 quick steps to activate your digital storefront and start receiving customer orders.
        </p>
      </div>

      {/* ═══════════ RESPONSIVE STEP PROGRESS BAR ═══════════ */}
      {/* 1. Mobile Stepper (Under 640px) */}
      <div className="onboarding-steps-mobile" style={{ maxWidth: "780px", margin: "0 auto 24px", padding: "14px 18px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
            Step {currentStep} of 6
          </span>
          <strong style={{ fontSize: "13px", color: "#0f172a" }}>
            {stepsList[currentStep - 1]?.label}
          </strong>
        </div>
        <div style={{ width: "100%", height: "6px", background: "#f1f5f9", borderRadius: "99px", overflow: "hidden" }}>
          <div
            style={{
              width: `${(currentStep / 6) * 100}%`,
              height: "100%",
              background: "linear-gradient(90deg, #e11d48, #f43f5e)",
              borderRadius: "99px",
              transition: "width 0.3s ease"
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
          {stepsList.map((s) => (
            <span
              key={s.num}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: currentStep >= s.num ? "var(--primary)" : "#cbd5e1"
              }}
            />
          ))}
        </div>
      </div>

      {/* 2. Desktop Stepper (640px+) */}
      <div className="onboarding-steps-desktop onboarding-steps-scroll" style={{ maxWidth: "780px", margin: "0 auto 32px", padding: "0 10px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
          {stepsList.map((s, idx) => {
            const isCompleted = currentStep > s.num;
            const isCurrent = currentStep === s.num;
            return (
              <React.Fragment key={s.num}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", zIndex: 2 }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: isCompleted ? "#16a34a" : isCurrent ? "var(--primary)" : "#e2e8f0",
                      color: isCompleted || isCurrent ? "#ffffff" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: "800",
                      boxShadow: isCurrent ? "0 0 0 4px rgba(225, 29, 72, 0.15)" : "none",
                      transition: "all 0.2s"
                    }}
                  >
                    {isCompleted ? "✓" : s.num}
                  </div>
                  <span
                    style={{
                      fontSize: "11.5px",
                      fontWeight: isCurrent ? "800" : "600",
                      color: isCurrent ? "var(--primary)" : isCompleted ? "#0f172a" : "#94a3b8",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < stepsList.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: "3px",
                      background: currentStep > idx + 1 ? "#16a34a" : "#e2e8f0",
                      margin: "0 8px 20px",
                      zIndex: 1,
                      borderRadius: "2px"
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ═══════════ FORM CONTAINER ═══════════ */}
      <div
        className="card-brand onboarding-form-box"
        style={{
          maxWidth: "780px",
          margin: "0 auto",
          background: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
        }}
      >
        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              padding: "12px 16px",
              marginBottom: "24px",
              fontSize: "13.5px",
              color: "#b91c1c",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            <AlertCircleIcon size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* ── STEP 1: SELLER ACCOUNT ── */}
        {currentStep === 1 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <span className="section-badge">STEP 1 OF 6</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Confirm Seller Account
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                You are currently signed in. Your store profile will be linked to this account.
              </p>
            </div>

            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    background: "#faedea",
                    color: "var(--primary)",
                    display: "grid",
                    placeItems: "center",
                    fontSize: "18px",
                    fontWeight: "900"
                  }}
                >
                  {user?.firstName?.charAt(0) || "S"}
                </div>
                <div>
                  <h4 style={{ fontSize: "16px", fontWeight: "800", color: "#0f172a", margin: 0 }}>
                    {user?.firstName} {user?.lastName}
                  </h4>
                  <p style={{ fontSize: "13px", color: "#64748b", margin: "2px 0 0" }}>
                    {user?.email} • {user?.number || "No phone added"}
                  </p>
                  <span style={{ display: "inline-block", background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "800", padding: "2px 8px", borderRadius: "6px", marginTop: "6px" }}>
                    ✓ Authenticated User
                  </span>
                </div>
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                style={{ padding: "10px 28px", fontSize: "14px", fontWeight: "800" }}
              >
                Continue to Business Information &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: BUSINESS INFORMATION ── */}
        {currentStep === 2 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <span className="section-badge">STEP 2 OF 6</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Business &amp; Store Information
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                Provide details about your storefront and physical fulfillment location.
              </p>
            </div>

            <div className="form-section" style={{ marginBottom: "20px" }}>
              {/* GPS Location Auto-Detection Banner */}
              <div style={{
                background: "#f0fdf4",
                border: "1.5px solid #bbf7d0",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: "#dcfce7",
                    color: "#166534",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0
                  }}>
                    <MapPinIcon size={20} />
                  </div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "800", color: "#14532d" }}>
                      Shop GPS Location Auto-Detection
                    </div>
                    <div style={{ fontSize: "12.5px", color: "#166534", marginTop: "2px" }}>
                      {gpsStatus === "detecting" && "Detecting exact shop location..."}
                      {gpsStatus === "detected" && (gpsMessage || "Location accurately pinned on map.")}
                      {gpsStatus === "error" && (gpsMessage || "Could not detect location automatically.")}
                      {gpsStatus === "idle" && "Click detect to pin your shop location automatically."}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => detectShopLocation(true)}
                  disabled={gpsStatus === "detecting"}
                  style={{
                    background: "#ffffff",
                    border: "1.5px solid #86efac",
                    color: "#166534",
                    padding: "7px 14px",
                    borderRadius: "8px",
                    fontSize: "12.5px",
                    fontWeight: "800",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                >
                  <RefreshCwIcon size={14} className={gpsStatus === "detecting" ? "spin" : ""} />
                  {gpsStatus === "detecting" ? "Detecting..." : "Re-Detect Current Shop Location"}
                </button>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Store / Business Name <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Siiwan Footwear &amp; Apparel"
                />
                <p className="form-hint" style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "4px" }}>
                  This will be your customer-facing marketplace store name.
                </p>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Business Type <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <select className="form-select" value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
                  <option value="individual">Individual / Sole Proprietor</option>
                  <option value="proprietorship">Registered Proprietorship</option>
                  <option value="partnership">Partnership Firm</option>
                  <option value="company">Private / Public Limited Company</option>
                  <option value="other">Other Entity</option>
                </select>
              </div>

              <div className="row">
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>
                    Business Contact Phone <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    maxLength="10"
                    className="form-control"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile number"
                  />
                </div>
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>
                    Business Contact Email <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="business@example.com"
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Storefront Physical Address <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Shop No, Building Name, Street Name"
                />
              </div>

              <div className="row">
                <div className="col-md-4" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>City <span style={{ color: "var(--primary)" }}>*</span></label>
                  <input type="text" className="form-control" required value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
                </div>
                <div className="col-md-4" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>State <span style={{ color: "var(--primary)" }}>*</span></label>
                  <input type="text" className="form-control" required value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
                </div>
                <div className="col-md-4" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>Pincode <span style={{ color: "var(--primary)" }}>*</span></label>
                  <input type="text" maxLength="6" className="form-control" required value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" />
                </div>
              </div>

              {/* Godown / Warehouse Address Section */}
              <div style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "12px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0"
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={hasGodown}
                    onChange={(e) => setHasGodown(e.target.checked)}
                    style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "var(--primary)" }}
                  />
                  <div>
                    <strong style={{ fontSize: "14px", color: "#0f172a", display: "block" }}>
                      Add Separate Godown / Warehouse Address
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Optional &mdash; Enable if courier pickup or dispatch takes place from a different godown or storage facility.
                    </span>
                  </div>
                </label>

                {hasGodown && (
                  <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
                    <div className="row">
                      <div className="col-md-6" style={{ marginBottom: "14px" }}>
                        <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown Contact Person</label>
                        <input
                          type="text"
                          className="form-control"
                          value={godownContactName}
                          onChange={(e) => setGodownContactName(e.target.value)}
                          placeholder="e.g. Ramesh Kumar"
                        />
                      </div>
                      <div className="col-md-6" style={{ marginBottom: "14px" }}>
                        <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown Phone</label>
                        <input
                          type="tel"
                          maxLength="10"
                          className="form-control"
                          value={godownPhone}
                          onChange={(e) => setGodownPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="10-digit mobile number"
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: "14px" }}>
                      <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown Street Address</label>
                      <input
                        type="text"
                        className="form-control"
                        value={godownStreet}
                        onChange={(e) => setGodownStreet(e.target.value)}
                        placeholder="Godown No, Industrial Area, Landmark"
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-4" style={{ marginBottom: "14px" }}>
                        <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={godownCity}
                          onChange={(e) => setGodownCity(e.target.value)}
                          placeholder="City"
                        />
                      </div>
                      <div className="col-md-4" style={{ marginBottom: "14px" }}>
                        <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown State</label>
                        <input
                          type="text"
                          className="form-control"
                          value={godownState}
                          onChange={(e) => setGodownState(e.target.value)}
                          placeholder="State"
                        />
                      </div>
                      <div className="col-md-4" style={{ marginBottom: "14px" }}>
                        <label className="form-label" style={{ fontWeight: "700", fontSize: "12.5px" }}>Godown Pincode</label>
                        <input
                          type="text"
                          maxLength="6"
                          className="form-control"
                          value={godownPincode}
                          onChange={(e) => setGodownPincode(e.target.value.replace(/\D/g, ""))}
                          placeholder="6-digit PIN"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                &larr; Back
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Continue to Identity &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: IDENTITY INFORMATION ── */}
        {currentStep === 3 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <span className="section-badge">STEP 3 OF 6</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Identity Numbers Verification
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                Enter your official Aadhaar and PAN numbers. Sensitive details are protected and masked.
              </p>
            </div>

            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
              <div style={{ marginBottom: "20px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Aadhaar Number (12 Digits) <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={aadhaarNumber}
                  onChange={handleAadhaarChange}
                  placeholder="XXXX XXXX XXXX"
                  style={{ letterSpacing: "2px", fontWeight: "700" }}
                />
                <p className="form-hint" style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "4px" }}>
                  Your complete Aadhaar number is encrypted and never exposed in plaintext.
                </p>
              </div>

              <div>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  PAN Card Number (10 Alphanumeric Characters) <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <input
                  type="text"
                  maxLength="10"
                  className="form-control"
                  required
                  value={panNumber}
                  onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F"
                  style={{ letterSpacing: "2px", fontWeight: "700", textTransform: "uppercase" }}
                />
                <p className="form-hint" style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "4px" }}>
                  Format: 5 letters, 4 digits, 1 letter.
                </p>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                &larr; Back
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Continue to Document Upload &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: DOCUMENT UPLOADS ── */}
        {currentStep === 4 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "26px" }}>
              <span className="section-badge">STEP 4 OF 6</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Upload Identity Documents
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                Upload clear photos or scans of your Aadhaar (Front &amp; Back) and PAN Card. Max 5MB per file.
              </p>
            </div>

            {/* Live Success Banner */}
            {docSuccessNotice && (
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1.5px solid #86efac",
                  color: "#15803d",
                  padding: "12px 18px",
                  borderRadius: "10px",
                  fontSize: "13.5px",
                  fontWeight: "700",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px rgba(34, 197, 94, 0.15)",
                }}
              >
                <span>{docSuccessNotice}</span>
                <button
                  type="button"
                  onClick={() => setDocSuccessNotice("")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#15803d", fontWeight: "900", fontSize: "14px" }}
                >
                  ✕
                </button>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "18px", marginBottom: "26px" }}>
              {/* 1. Aadhaar Front Card */}
              {renderDocumentUploadCard(
                "aadhaar_front",
                "Aadhaar Card — Front Side",
                "Contains your official photo, full name, and Date of Birth",
                true,
                aadhaarFrontFile
              )}

              {/* 2. Aadhaar Back Card */}
              {renderDocumentUploadCard(
                "aadhaar_back",
                "Aadhaar Card — Back Side",
                "Contains your registered address and barcode details",
                true,
                aadhaarBackFile
              )}

              {/* 3. PAN Card */}
              {renderDocumentUploadCard(
                "pan",
                "PAN Card",
                "Government of India Permanent Account Number card",
                true,
                panFile
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                &larr; Back
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Continue to Bank &amp; Payout &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 5: BANK & PAYOUT DETAILS ── */}
        {currentStep === 5 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <span className="section-badge">STEP 5 OF 6</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Bank &amp; Payout Information
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                Customer order payments and settlement earnings will be deposited directly to this bank account.
              </p>
            </div>

            <div className="form-section" style={{ marginBottom: "20px" }}>
              {/* 1. Account Holder Name */}
              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Account Holder Name <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  placeholder="As registered in your bank account"
                />
              </div>

              {/* 2. IFSC Code */}
              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  IFSC Code <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    maxLength="11"
                    className="form-control"
                    required
                    value={ifsc}
                    onChange={(e) => handleIfscLookup(e.target.value)}
                    placeholder="e.g. SBIN0001234, HDFC0001234"
                    style={{ textTransform: "uppercase", fontFamily: "monospace", letterSpacing: "1px" }}
                  />
                  {ifscLoading && (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      style={{ position: "absolute", right: "12px", top: "12px" }}
                    />
                  )}
                </div>
                {ifscError && (
                  <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginTop: "4px" }}>
                    ✕ {ifscError}
                  </div>
                )}
                {ifscSuccess && (
                  <div style={{ fontSize: "12px", color: "#166534", fontWeight: "700", marginTop: "4px", background: "#f0fdf4", padding: "6px 10px", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                    ✓ {ifscSuccess.bankName} &middot; {ifscSuccess.branch} ({ifscSuccess.city})
                  </div>
                )}
              </div>

              {/* 3. Auto-Identified Bank Name (Locked) */}
              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  Bank Name (Auto-Identified) <span style={{ color: "#64748b" }}>[Locked]</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  disabled
                  readOnly
                  value={bankName || "Automatically identified from IFSC code"}
                  style={{ background: "#f1f5f9", fontWeight: "700", cursor: "not-allowed" }}
                />
              </div>

              {/* 4. Dual Account Numbers */}
              <div className="row">
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>
                    Account Number <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showAcc ? "text" : "password"}
                      className="form-control"
                      required
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter bank account number"
                      style={{ paddingRight: "38px", fontFamily: showAcc ? "monospace" : "inherit" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAcc(!showAcc)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        fontSize: "15px",
                        cursor: "pointer",
                        padding: "4px"
                      }}
                      title={showAcc ? "Hide account number" : "Show account number"}
                    >
                      {showAcc ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                </div>
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>
                    Confirm Account Number <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmAcc ? "text" : "password"}
                      className="form-control"
                      required
                      value={confirmAccountNumber}
                      onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ""))}
                      placeholder="Re-enter bank account number"
                      style={{ paddingRight: "38px", fontFamily: showConfirmAcc ? "monospace" : "inherit" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmAcc(!showConfirmAcc)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        fontSize: "15px",
                        cursor: "pointer",
                        padding: "4px"
                      }}
                      title={showConfirmAcc ? "Hide account number" : "Show account number"}
                    >
                      {showConfirmAcc ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                </div>
              </div>

              {accountNumber && confirmAccountNumber && accountNumber === confirmAccountNumber && (
                <div style={{ fontSize: "12px", color: "#166534", fontWeight: "700", marginBottom: "12px" }}>
                  ✓ Account numbers match
                </div>
              )}
              {accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber && (
                <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginBottom: "12px" }}>
                  ✕ Account numbers do not match
                </div>
              )}

              <div className="row">
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>Account Type</label>
                  <select className="form-select" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                    <option value="current">Current Account (Recommended for Business)</option>
                    <option value="savings">Savings Account (Personal)</option>
                  </select>
                </div>
                <div className="col-md-6" style={{ marginBottom: "16px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>UPI ID / VPA (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="merchant@okhdfcbank"
                  />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                &larr; Back
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Continue to Review &amp; Submit &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 6: REVIEW & LEGAL CONSENTS ── */}
        {currentStep === 6 && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <span className="section-badge">FINAL STEP</span>
              <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#0f172a", marginTop: "8px" }}>
                Review Application &amp; Legal Consent
              </h3>
              <p style={{ fontSize: "13.5px", color: "#64748b" }}>
                Please review your application summary and confirm required verification consents.
              </p>
            </div>

            {/* Application Summary Box */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", fontSize: "13px" }}>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Store Name</span>
                  <strong style={{ color: "#0f172a" }}>{businessName}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Business Type</span>
                  <strong style={{ color: "#0f172a", textTransform: "capitalize" }}>{businessType}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Aadhaar Number</span>
                  <strong style={{ color: "#0f172a" }}>•••• •••• {aadhaarNumber.replace(/\s+/g, "").slice(-4)}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>PAN Number</span>
                  <strong style={{ color: "#0f172a" }}>{panNumber.slice(0, 5)}••••{panNumber.slice(-1)}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Bank Account</span>
                  <strong style={{ color: "#0f172a" }}>{bankName} (•••• {accountNumber.slice(-4)})</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Documents Attached</span>
                  <strong style={{ color: "#16a34a" }}>3 Required Files Uploaded ✓</strong>
                </div>
              </div>
            </div>

            {/* Legal / Privacy Consents */}
            <div style={{ border: "1.5px solid #fed7aa", background: "#fffaf5", borderRadius: "12px", padding: "18px", marginBottom: "24px" }}>
              <strong style={{ fontSize: "13.5px", color: "#9a3412", display: "block", marginBottom: "12px" }}>
                Mandatory Declarations &amp; Legal Consent
              </strong>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={consentAccuracy}
                    onChange={(e) => setConsentAccuracy(e.target.checked)}
                    style={{ marginTop: "3px" }}
                  />
                  <span>I confirm that the business information and identity documents provided are true, complete, and accurate.</span>
                </label>

                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={consentVerification}
                    onChange={(e) => setConsentVerification(e.target.checked)}
                    style={{ marginTop: "3px" }}
                  />
                  <span>I authorize IMSHOP / IM Online to verify my identity, business registration, and bank payout information.</span>
                </label>

                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={consentTerms}
                    onChange={(e) => setConsentTerms(e.target.checked)}
                    style={{ marginTop: "3px" }}
                  />
                  <span>I agree to the IMSHOP Seller Terms of Service, Merchant Fulfillment Guidelines, and Privacy Policy.</span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={handleBack} disabled={loading}>
                &larr; Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading || !consentAccuracy || !consentVerification || !consentTerms}
                style={{ padding: "12px 32px", fontSize: "14px", fontWeight: "800" }}
              >
                {loading ? (
                  <span>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Submitting Application...
                  </span>
                ) : (
                  "Submit Seller Application &rarr;"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Apply;
